import { toCamelCaseDeep } from 'src/utils/transform';
import MemriseApi from '../MemriseApi';
import { SuccessResponse } from '../types';
import * as Types from './types';
import * as Schema from './schema';

export default class WordlistsApiClass extends MemriseApi {
  constructor() {
    super({ endpointPrefix: '/' });
  }

  public async getWordlists(languagePairId: number): Promise<Types.WordlistsResponse> {
    const response = await this.get<Schema.WordlistsResponse>(
      `me/language_pairs/${languagePairId}/wordlists/?with_progress=false`,
    );
    return toCamelCaseDeep(response);
  }

  public async getWordlistsWithProgress(
    languagePairId: number,
  ): Promise<Types.WordlistsWithProgressResponse> {
    const response = await this.get<Schema.WordlistsResponse>(
      `me/language_pairs/${languagePairId}/wordlists/?with_progress=true`,
    );
    return toCamelCaseDeep(response);
  }

  public async getWordlist(
    languagePairId: number,
    wordlistId: string,
  ): Promise<Types.WordlistResponse> {
    const response = await this.get<Schema.WordlistResponse>(
      `me/language_pairs/${languagePairId}/wordlists/${wordlistId}/`,
    );
    return toCamelCaseDeep(response);
  }

  public async getWordlistLearnables(
    languagePairId: number,
    wordlistId: string,
    params: Schema.LearnablesStateRequest,
  ): Promise<Types.LearnablesStateResponse> {
    const response = await this.get<Schema.LearnablesStateResponse, Schema.LearnablesStateRequest>(
      `me/language_pairs/${languagePairId}/wordlists/${wordlistId}/learnables/`,
      params,
    );
    return toCamelCaseDeep(response);
  }

  public async activateWordlist(
    languagePairId: number,
    wordlistId: string,
  ): Promise<SuccessResponse> {
    return this.post(`me/language_pairs/${languagePairId}/wordlists/${wordlistId}/activate/`, {});
  }

  public async addLearnablesToWordlist(
    languagePairId: number,
    learnableIds: number[],
  ): Promise<SuccessResponse> {
    return this.post(`me/language_pairs/${languagePairId}/wordlists/personal/update_learnables/`, {
      bodyObject: { learnable_ids: learnableIds },
    });
  }

  public async removeLearnablesFromWordlist(
    languagePairId: number,
    learnableIds: number[],
  ): Promise<SuccessResponse> {
    return this.delete(
      `me/language_pairs/${languagePairId}/wordlists/personal/update_learnables/`,
      {
        bodyObject: { learnable_ids: learnableIds },
      },
    );
  }

  public async getLearningGoals(
    languagePairId: number,
    skillLevelId: number | null,
  ): Promise<Types.LearningGoalsResponse> {
    const response = await this.get<Schema.LearningGoalsResponse, Schema.LearningGoalsRequest>(
      `/language_pairs/${languagePairId}/learning_goals/`,
      { skill_level_id: skillLevelId },
    );
    return toCamelCaseDeep(response);
  }

  public async submitLearningGoals(
    languagePairId: number,
    skillLevelId: number | null,
    learningGoalIds: number[],
  ): Promise<Types.SubmitLearningGoalsResponse> {
    const response = await this.post<
      Schema.SubmitLearningGoalsResponse,
      Schema.SubmitLearningGoalsRequest
    >(`me/language_pairs/${languagePairId}/learning_goals/`, {
      bodyObject: {
        learning_goal_ids: learningGoalIds,
        ...(skillLevelId ? { skill_level_id: skillLevelId } : {}),
      },
    });
    return toCamelCaseDeep(response);
  }

  public async setWordlistCollection(
    languagePairId: number,
    wordlistIds: string[],
  ): Promise<SuccessResponse> {
    const response = await this.post<SuccessResponse, Schema.WordlistCollectionRequest>(
      `me/language_pairs/${languagePairId}/wordlists/collection/`,
      {
        bodyObject: { wordlist_ids: wordlistIds },
      },
    );
    return toCamelCaseDeep(response);
  }
}

export const WordlistsApi = new WordlistsApiClass();
